import { useEffect, useState, useCallback } from 'react';
import { supabase, handleSupabaseError, retryOperation, isNotFoundError } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { Plant } from '../types/plant';
import debounce from 'lodash.debounce';

export function usePlantDetails(id: string | undefined) {
  const [plant, setPlant] = useState<Plant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isInGarden, setIsInGarden] = useState(false);
  const { user } = useAuth();

  // Create a debounced fetch function that will be stable across renders
  const debouncedFetch = useCallback(
    debounce(async (
      plantId: string,
      controller: AbortController,
      mounted: { current: boolean }
    ) => {
      try {
        if (!mounted.current) return;

        const { data: plantData, error: plantError } = await retryOperation(
          () => supabase
            .from('plants')
            .select('*')
            .eq('id', plantId)
            .single(),
          3,
          1000,
          controller.signal
        );

        if (plantError) {
          if (isNotFoundError(plantError)) {
            if (mounted.current) {
              setError('Plant not found');
              setLoading(false);
            }
            return;
          }
          throw plantError;
        }

        if (!plantData) {
          if (mounted.current) {
            setError('Plant not found');
            setLoading(false);
          }
          return;
        }

        // Batch all related data requests together
        const [
          gardenResult,
          companionResult,
          reverseCompanionResult,
          varietiesResult,
          pestsResult,
          diseasesResult
        ] = await Promise.all([
          // Garden check
          user ? retryOperation(
            () => supabase
              .from('user_gardens')
              .select('id')
              .eq('user_id', user.id)
              .eq('plant_id', plantId)
              .maybeSingle(),
            3,
            1000,
            controller.signal
          ) : Promise.resolve({ data: null, error: null }),

          // Companion plants
          retryOperation(
            () => supabase
              .from('companion_plants')
              .select(`
                id,
                benefits,
                companion_plant:plants!companion_plants_companion_plant_id_fkey (
                  id,
                  name,
                  scientific_name,
                  image_url,
                  sunlight_requirements,
                  water_requirements
                )
              `)
              .eq('plant_id', plantId),
            3,
            1000,
            controller.signal
          ),

          // Reverse companions
          retryOperation(
            () => supabase
              .from('companion_plants')
              .select(`
                id,
                benefits,
                plant:plants!companion_plants_plant_id_fkey (
                  id,
                  name,
                  scientific_name,
                  image_url,
                  sunlight_requirements,
                  water_requirements
                )
              `)
              .eq('companion_plant_id', plantId),
            3,
            1000,
            controller.signal
          ),

          // Varieties
          retryOperation(
            () => supabase
              .from('plant_varieties')
              .select('*')
              .eq('plant_id', plantId)
              .order('name'),
            3,
            1000,
            controller.signal
          ),

          // Pests
          retryOperation(
            () => supabase
              .from('plant_pests')
              .select('*')
              .eq('plant_id', plantId)
              .order('pest_name'),
            3,
            1000,
            controller.signal
          ),

          // Diseases
          retryOperation(
            () => supabase
              .from('plant_diseases')
              .select('*')
              .eq('plant_id', plantId)
              .order('disease_name'),
            3,
            1000,
            controller.signal
          )
        ]);

        // Handle any errors from the batch requests
        const errors = [
          gardenResult.error,
          companionResult.error,
          reverseCompanionResult.error,
          varietiesResult.error,
          pestsResult.error,
          diseasesResult.error
        ].filter(error => error && !isNotFoundError(error));

        if (errors.length > 0) {
          throw errors[0];
        }

        // Transform pest and disease data
        const transformedPests = pestsResult.data?.map(pest => ({
          id: pest.id,
          name: pest.pest_name,
          description: pest.description,
          solution: pest.solution,
          severity: pest.severity
        }));

        const transformedDiseases = diseasesResult.data?.map(disease => ({
          id: disease.id,
          name: disease.disease_name,
          description: disease.description,
          solution: disease.solution,
          severity: disease.severity
        }));

        if (mounted.current && !controller.signal.aborted) {
          setPlant({
            ...plantData,
            companion_plants: companionResult.data || [],
            companion_plants_reverse: reverseCompanionResult.data || [],
            varieties: varietiesResult.data || [],
            pests: transformedPests || [],
            diseases: transformedDiseases || []
          });
          setIsInGarden(!!gardenResult.data);
          setError(null);
          setLoading(false);
        }
      } catch (err) {
        if (mounted.current && !controller.signal.aborted) {
          const errorMessage = handleSupabaseError(err);
          console.error('Error fetching plant details:', err);
          setError(errorMessage);
          setPlant(null);
          setLoading(false);
        }
      }
    }, 300), // Debounce for 300ms
    [] // Empty deps array since we want this to be stable
  );

  useEffect(() => {
    const mounted = { current: true };
    const controller = new AbortController();

    async function fetchPlant() {
      if (!id) {
        if (mounted.current) {
          setError('Plant ID is required');
          setLoading(false);
        }
        return;
      }

      if (mounted.current) {
        setLoading(true);
        setError(null);
      }

      // Use the debounced fetch
      debouncedFetch(id, controller, mounted);
    }

    fetchPlant();

    return () => {
      mounted.current = false;
      controller.abort();
      debouncedFetch.cancel(); // Cancel any pending debounced calls
    };
  }, [id, user, debouncedFetch]);

  return { plant, loading, error, isInGarden };
}